import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';
import { FC, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import moment from 'moment';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import PruFilter, { PruFilterDropdownItem, PruFilterItemType } from 'src/app/common/components/PruTable/PruFilter';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { ModulePermissionProps } from 'src/app/common/module/types';
import { getDayEnd, getDayStart, useDataProvider } from 'src/app/common/utils';

import { WarningAmber } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import {
  ACTIVITY_TRACKING_BASE_PATH,
  FRAMEWORK_OPTIONS,
  QR_CODE_ACTIVITY_TYPE_PATH,
  STATUS_OPTIONS,
} from '../../../constans';
import {
  fetchQRCodeActivityCategoryList,
  fetchQRCodeActivityTypeList,
  updateStatusQRCodeActivityType,
} from '../../../network/activityTrackingCrud';
import {
  QRCodeActivityTypeApiPaginateList,
  QRCodeActivityTypeApiResponse,
} from '../../../types/activity-tracking-api-types';
import { EStatus, QRCodeActivityTypeListParam } from '../../../types/activity-tracking-types';

type QRCodeActivityTypeListPageProps = { initialParams?: QRCodeActivityTypeListParam } & ParamsProps &
  ModulePermissionProps;

const initialState: QRCodeActivityTypeListParam = {
  name: '',
  accessibility: '',
  framework: '',
  createdAtFrom: null,
  createdAtTo: null,
  status: '',
  categoryId: '',
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: QRCodeActivityTypeListParam): QRCodeActivityTypeListParam => {
  return initialParams
    ? {
        name: initialParams.name || '',
        categoryId: initialParams.categoryId || '',
        accessibility: initialParams.accessibility || '',
        framework: initialParams.framework || '',
        createdAtFrom: initialParams.createdAtFrom ? getDayStart(new Date(initialParams.createdAtFrom)) : null,
        createdAtTo: initialParams.createdAtTo ? getDayEnd(new Date(initialParams.createdAtTo)) : null,
        status: initialParams.status || '',
        page: 1,
        limit: 5,
      }
    : initialState;
};

const QRCodeActivityTypeListPage: FC<QRCodeActivityTypeListPageProps> = ({
  initialParams,
  onChangeQueryParams,
  enableCreate,
  enableUpdate,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const Translation = (id: string) => intl.formatMessage({ id });

  const [filterState, setFilterState] = useState<QRCodeActivityTypeListParam>(paramsInitiator(initialParams));
  const [typeList, setTypeList] = useState<QRCodeActivityTypeApiPaginateList>();
  const [categoryOptions, setCategoryOptions] = useState<PruFilterDropdownItem[]>([]);
  const filterStateRef = useRef(filterState);
  const pinnedItemRef = useRef<QRCodeActivityTypeApiResponse | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [_, startTransition] = useTransition();

  const categoriesOptions = useMemo(() => [{ displayName: 'All', value: '' }, ...categoryOptions], [categoryOptions]);

  const { isLoading, refreshData } = useDataProvider<QRCodeActivityTypeApiPaginateList>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterStateRef.current);
        }
        const result = await fetchQRCodeActivityTypeList(filterStateRef.current, dispatch);
        if (pinnedItemRef.current && filterStateRef.current.page === 1) {
          const pinned = pinnedItemRef.current;
          const filteredItems = result.items.filter((i) => i._id !== pinned._id);
          return { ...result, items: [pinned, ...filteredItems].slice(0, filterStateRef.current.limit ?? 5) };
        }
        return result;
      } catch (err) {}
    },
    setTypeList,
    false,
  );

  const handleToggleStatus = async (type: QRCodeActivityTypeApiResponse, status: EStatus) => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const payload = {
        ...type,
        categoryId: type.category._id,
        status: status,
      };
      const response = await updateStatusQRCodeActivityType(
        type._id,
        payload,
        dispatch,
        abortControllerRef.current.signal,
      );

      if (!response.isSuccess) {
        const error = response.error;
        if (error?.code === 'SORT_ORDER_DUPLICATE_MESSAGE') {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: 'Error',
                content: `Display Order number are used. Please input another number.`,
              },
            ]),
          );
          return;
        }
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.ERROR,
              title: 'Error',
              content: `${
                status === EStatus.Active
                  ? Translation('activityTracking.notify.publishFailed')
                  : Translation('activityTracking.notify.unpublishFailed')
              } - ${type.name}`,
            },
          ]),
        );
        return;
      }
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `${
              status === EStatus.Active
                ? Translation('activityTracking.notify.publishSuccess')
                : Translation('activityTracking.notify.unpublishSuccess')
            } - ${type.name}`,
          },
        ]),
      );
      refreshData();
    } catch (err) {
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.ERROR,
            title: 'Error',
            content: `${
              status === EStatus.Active
                ? Translation('activityTracking.notify.publishFailed')
                : Translation('activityTracking.notify.unpublishFailed')
            } - ${type.name}`,
          },
        ]),
      );
    }
  };

  const detailPath = `${ACTIVITY_TRACKING_BASE_PATH}${QR_CODE_ACTIVITY_TYPE_PATH}`;

  useEffect(() => {
    fetchQRCodeActivityCategoryList({ page: 1, limit: 1000 }, dispatch)
      .then((result) => {
        const opts = result.items.map((c) => ({ value: c._id, displayName: c.name })) as PruFilterDropdownItem[];
        setCategoryOptions(opts);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      <PruFilter
        title={Translation('activityTracking.qrCodeActivityType.filter')}
        itemDef={[
          {
            type: PruFilterItemType.FREE_TEXT,
            field: 'name',
            initialValue: filterState.name,
            displayName: Translation('activityTracking.qrCodeActivityType.typeName'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'framework',
            initialValue: filterState.framework,
            displayName: Translation('activityTracking.qrCodeActivityType.framework'),
            choices: FRAMEWORK_OPTIONS,
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'categoryId',
            initialValue: filterState.categoryId,
            displayName: Translation('activityTracking.qrCodeActivityType.category'),
            choices: categoriesOptions,
          },
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'createdAtFrom',
            fieldTo: 'createdAtTo',
            initialDateFrom: filterState.createdAtFrom,
            initialDateTo: filterState.createdAtTo,
            displayName: Translation('activityTracking.qrCodeActivityType.createdTime'),
          },
          {
            type: PruFilterItemType.DROPDOWN,
            field: 'status',
            initialValue: filterState.status,
            displayName: Translation('activityTracking.qrCodeActivityType.status'),
            choices: STATUS_OPTIONS,
          },
        ]}
        onChangeFilter={(data) => {
          pinnedItemRef.current = null;
          const next = {
            ...filterState,
            name: data.name,
            framework: data.framework,
            categoryId: data.categoryId,
            createdAtFrom: data.createdAtFrom,
            createdAtTo: data.createdAtTo,
            status: data.status,
          };
          filterStateRef.current = next;
          setFilterState(next);
        }}
        fetchData={refreshData}
      />

      <PruTable
        disableBulkSelect
        dataSource={typeList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('activityTracking.qrCodeActivityType.list')}
        headerBtnDef={
          enableCreate
            ? [
                {
                  color: 'primary',
                  title: Translation('app.button.add'),
                  onClick: () => history.push(`${detailPath}/create`),
                },
              ]
            : []
        }
        operationDef={[
          {
            title: Translation('section.common.operation.view'),
            tooltipText: 'View Activity Type',
            onClick: (row) => history.push(`${detailPath}/view/${row.originId ?? row._id}`),
          },
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Activity Type',
            condition: (row) => enableUpdate && row.status === EStatus.InActive,
            onClick: (row) => history.push(`${detailPath}/edit/${row.originId ?? row._id}`),
          },
          {
            title: Translation('activityTracking.action.publish'),
            tooltipText: 'Publish Activity Type',
            condition: (row) => enableUpdate && row.status === EStatus.InActive,
            onClick: (row) =>
              startTransition(() => {
                handleToggleStatus(row, EStatus.Active);
              }),
          },
          {
            title: Translation('activityTracking.action.unpublish'),
            tooltipText: 'Unpublish Activity Type',
            condition: (row) => enableUpdate && row.status === EStatus.Active,
            onClick: (row) =>
              startTransition(() => {
                handleToggleStatus(row, EStatus.InActive);
              }),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: 'id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'name',
            displayName: Translation('activityTracking.qrCodeActivityType.typeName'),
            renderData: (row) => row.name,
          },
          {
            keyIndex: 'category',
            displayName: Translation('activityTracking.qrCodeActivityType.category'),
            renderData: (row) =>
              row?.category?.isLatest ? (
                String(row?.category?.name)
              ) : (
                <span>
                  <Tooltip
                    title="Category is outdated."
                    placement="right-start"
                    style={{
                      marginRight: 8,
                    }}
                  >
                    <WarningAmber color="warning" fontSize="small" />
                  </Tooltip>
                  {row?.category?.name}
                </span>
              ),
          },
          {
            keyIndex: 'framework',
            displayName: Translation('activityTracking.qrCodeActivityType.framework'),
            renderData: (row) => String(row?.category?.framework),
          },
          {
            keyIndex: 'sortOrder',
            displayName: 'Display Order',
            renderData: (row) => String(row?.sortOrder ?? '-'),
          },
          {
            keyIndex: 'status',
            displayName: Translation('activityTracking.qrCodeActivityType.status'),
            renderData: (row) => String(row.status),
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('activityTracking.qrCodeActivityType.createdTime'),
            renderData: (row) => (row.createdAt ? moment(row.createdAt).format('DD/MM/YYYY HH:mm:ss') : '-'),
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('activityTracking.qrCodeActivityType.lastUpdatedTime'),
            renderData: (row) => (row.updatedAt ? moment(row.updatedAt).format('DD/MM/YYYY HH:mm:ss') : '-'),
          },
          {
            keyIndex: 'lastUpdatedBy',
            displayName: Translation('activityTracking.qrCodeActivityType.lastUpdatedBy'),
            renderData: (row) => row.lastUpdatedBy || '-',
          },
        ]}
        totalPages={typeList?.totalPages}
        totalRecords={typeList?.totalRecords}
        onChangePage={(page, rowsPerPage) => {
          const next = { ...filterState, page, limit: rowsPerPage };
          filterStateRef.current = next;
          setFilterState(next);
          refreshData();
        }}
      />
    </>
  );
};

export default QRCodeActivityTypeListPage;
